import { IoCreateOutline, IoEyeOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "reactstrap";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import IconButton from "../../../../components/Buttons/IconButton";
import DefaultPagination from "../../../../components/Pagination";
import AgGridTable from "../../../../components/Tables/AgGrid";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { get, del } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { useAuth } from "../../../../context/auth";
import { printProductionOrderStatus, isOrderEditable } from "../../utils/helpers";

function StatusCellRenderer({ value }) {
  return printProductionOrderStatus(value);
}

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate('/production/orders/view/' + data.id);
        }}
        size="sm" outline color="secondary"
      >
        <IoEyeOutline />
      </IconButton>
      {context.can('production.orders.edit') && isOrderEditable(data) && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.navigate('/production/orders/edit/' + data.id, { state: { productionOrderData: data } });
          }}
          size="sm" outline color="secondary"
        >
          <IoCreateOutline />
        </IconButton>
      )}
      {context.can('production.orders.delete') && data.status === 'draft' && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.setOrderToDelete(data.id);
          }}
          size="sm" outline color="danger"
        >
          <BsTrash3 />
        </IconButton>
      )}
    </div>
  );
}

function ProductionOrdersPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [orderToDelete, setOrderToDelete] = useState(null);

  const getProductionOrders = useCallback(async () => {
    setLoading(true);
    const query = `/production/orders?page=${currentPage}&page_size=${pageSize}` +
      (statusFilter ? `&status=${statusFilter}` : '');
    const data = await get(query);
    if(data?.success) {
      setRowData(data.production_orders.data);
      setTotalPages(data.production_orders.last_page);
      setTotalRecords(data.production_orders.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, statusFilter]);

  useEffect(() => {
    getProductionOrders();
  }, [getProductionOrders]);

  const deleteOrder = async () => {
    const data = await del('/production/orders/delete/' + orderToDelete);
    if(data?.success) {
      toast.success(t('production.productionOrderDeleted'));
      getProductionOrders();
    }
    setOrderToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { field: 'order_number', headerName: t('production.orderNumber'), flex: 1, sortable: true },
    {
      field: 'product.name',
      headerName: t('production.product'),
      flex: 2,
      sortable: true,
      valueGetter: (params) => params.data?.product?.name ?? params.data?.product_name ?? '-'
    },
    { field: 'quantity_planned', headerName: t('production.quantityPlanned'), flex: 1, sortable: true },
    { field: 'quantity_produced', headerName: t('production.quantityProduced'), flex: 1, sortable: true },
    { field: 'status', headerName: t('common.status'), flex: 1, sortable: true, cellRenderer: StatusCellRenderer },
    {
      field: 'total_cost',
      headerName: t('production.totalCost'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => Number(p.value ?? 0).toFixed(2)
    },
    {
      field: 'created_at',
      headerName: t('common.date'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => formatDate(p.value)
    },
    { headerName: '', width: 140, sortable: false, cellRenderer: ActionsCellRenderer }
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, t, can, setOrderToDelete }), [navigate, t, can]);

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('sidebar.productionOrders')} onReload={getProductionOrders} dataTotal={totalRecords}>
          <Input
            type="select"
            bsSize="sm"
            className="me-2"
            style={{ width: 'auto', display: 'inline-block' }}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">{t('production.allStatuses')}</option>
            <option value="draft">{t('common.draft')}</option>
            <option value="confirmed">{t('production.confirmed')}</option>
            <option value="in_progress">{t('production.inProgress')}</option>
            <option value="done">{t('production.done')}</option>
            <option value="cancelled">{t('production.cancelled')}</option>
          </Input>

          {can('production.orders.create') && (
            <DefaultButton color="primary" size="sm" onClick={() => navigate('/production/orders/create')}>
              {t('production.newProductionOrder')}
            </DefaultButton>
          )}
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={(e) => navigate('/production/orders/view/' + e.data.id)}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!orderToDelete}
        toggle={() => setOrderToDelete(null)}
        title={t('production.deleteProductionOrder')}
        message={t('production.deleteProductionOrderConfirm')}
        onConfirm={deleteOrder}
      />
    </SidebarLayout>
  );
}

export default ProductionOrdersPage;
